import {
  abs,
  billboarding,
  Discard,
  distance,
  float,
  Fn,
  If,
  instanceIndex,
  mod,
  mx_noise_float,
  positionLocal,
  sin,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from "three/tsl";
import Experience from "../Experience";
import { MeshDefaultMaterial } from "../Materials/MeshDefaultMaterial";
import { LANE_X } from "./TrackManager";
import {
  AdditiveBlending,
  Box3,
  BufferGeometry,
  CanvasTexture,
  Color,
  DoubleSide,
  Euler,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshBasicNodeMaterial,
  MeshLambertMaterial,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  Points,
  PointsMaterial,
  PointsNodeMaterial,
  Quaternion,
  SphereGeometry,
  TetrahedronGeometry,
  Texture,
  Vector3,
} from "three/webgpu";
import { DecalGeometry } from "three/addons/Addons";

export default class Train {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.resources = this.experience.resources;
    this.time = this.experience.time;
    this.debug = this.experience.debug;
    this.camera = this.experience.camera;

    //setup
    this.resource = {
      engine: this.resources.items.engineModel,
      boggie: this.resources.items.boggieModel,
      track: this.resources.items.trackModel,
    };
    this.elapsedTime = uniform(0.0);

    this.lane = 1;
    this._jumpFromX = LANE_X[1];
    this._jumpToX = LANE_X[1];
    this._jumpT = 1;
    this._jumpDuration = 0.3;
    this._jumpHeight = 0.7;

    this.setModel();
    // this.addDecal();
    // this.createTracks(); // moved to TrackManager
    this.createFreezingVapours();

    this.camera.setChaseTarget(this.group);
  }

  setLane(idx) {
    this.lane = idx;
    if (!this.group) return;
    this._jumpFromX = this.group.position.x;
    this._jumpToX = LANE_X[idx];
    this._jumpT = 0;
  }

  _tickJump(delta) {
    if (this._jumpT >= 1) return;
    this._jumpT = Math.min(1, this._jumpT + delta / this._jumpDuration);
    const eased = this._jumpT * this._jumpT * (3 - 2 * this._jumpT); // smoothstep
    this.group.position.x = this._jumpFromX + (this._jumpToX - this._jumpFromX) * eased;
    this.group.position.y = Math.sin(this._jumpT * Math.PI) * this._jumpHeight;
  }

  setModel() {
    this.group = new Group();
    this.engine = this.resource.engine.scene;
    const boggie1 = this.resource.boggie.scene.clone();
    const boggie2 = this.resource.boggie.scene.clone();
    boggie2.position.z -= 6.5;
    const boggie3 = this.resource.boggie.scene.clone();
    boggie3.position.z -= 13;
    const track1 = this.resource.track.scene.clone();
    const track2 = this.resource.track.scene.clone();

    const colorNode = Fn(() => {
      // const baseColor = this.game.terrain.colorNode(terrainData);

      // const slabTerrain = terrainData.r;
      // const slabNoiseUv = positionWorld.xz.mul(slabNoiseFrequency);
      // const slabNoise = texture(this.game.noises.perlin, slabNoiseUv).r;
      // const slabsTexture = texture(
      //   this.game.resources.floorSlabsTexture,
      //   positionWorld.xz.mul(slabTextureFrequency)
      // ).r;
      // const slabColor = mix(slabLowColor, slabHighColor, slabsTexture);
      // // return vec3(slabsTexture.mul(slabStrength))

      // const slab = slabTerrain.mul(slabNoise);
      // return vec3(slab)

      const finalColor = vec3(
        sin(positionLocal.x.add(this.elapsedTime)),
        abs(sin(positionLocal.y.add(this.elapsedTime))),
        abs(sin(positionLocal.z.add(this.elapsedTime)))
      ); //mix(baseColor, slabColor, slab);
      return finalColor;
    })();

    const material = new MeshDefaultMaterial({
      colorNode: colorNode,
      normalNode: vec3(0, 1, 0),
      // shadowNode: terrainData.g,
      hasWater: false,
      hasLightBounce: false,
      wireframe: false,
    });

    track1.traverse((child) => {
      if (child instanceof Mesh) {
        child.material = new MeshBasicMaterial({ color: "#2e2e2e" });
        child.position.x = this.engine.position.x + 0.4;
        child.position.z = -10;
        child.scale.z = 4.5;
        child.scale.x = 0.75;
      }
    });
    track2.traverse((child) => {
      if (child instanceof Mesh) {
        child.material = new MeshBasicMaterial({ color: "#2e2e2e" });
        child.position.x = this.engine.position.x - 0.4;
        child.position.z = -10;
        child.scale.z = 4.5;
        child.scale.x = 0.75;
      }
    });

    this.group.add(this.engine);
    this.group.add(boggie1);
    this.group.add(boggie2);
    this.group.add(boggie3);
    this.scene.add(this.group);
    // this.scene.add(track1);
    // this.scene.add(track2);
  }

  addDecal() {
    // 1. Target mesh — make sure its world matrix is up to date.
    const target = this.engine.children[0].children[2];
    this.engine.updateMatrixWorld(true);

    // 2. Canvas: rectangular matches text aspect better than 512×512.
    const canvas = document.querySelector("canvas.decal");
    canvas.width = 1024;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");

    // ctx.fillStyle = "cyan";
    // ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#D12E88";
    ctx.font = "bold 120px BBH Bartle";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("PORTRA", canvas.width / 2, canvas.height / 2);

    const decalTex = new CanvasTexture(canvas);

    // 3. Derive projector from target's world bounding box.
    const bbox = new Box3().setFromObject(target);
    const size = bbox.getSize(new Vector3());
    const center = bbox.getCenter(new Vector3());

    // Project from +X side, shooting in −X toward the engine's side face.
    // Orientation: default forward is −Z, so rotate Y by +90° to aim −X.
    const projectorPos = new Vector3(
      center.x + size.x,
      center.y - 0.15,
      center.z - 0.85
    );
    const projectorOrientation = new Euler(0, Math.PI / 2, 0);
    // width (Y on surface), height (Z on surface — mapped from projector's Y),
    // depth (X through the mesh — must be bigger than the mesh's X extent).
    const projectorSize = new Vector3(
      size.z * 0.45, // decal width along the side
      size.y * 0.45, // decal height along the side
      size.x * 1.5 // deep enough to pass through the mesh
    );

    // Debug: log where the projector is so you can sanity-check against the mesh.
    console.log("decal target bbox", {
      center,
      size,
      projectorPos,
      projectorSize,
    });

    const decalGeometry = new DecalGeometry(
      target,
      projectorPos,
      projectorOrientation,
      projectorSize
    );

    const decalMaterial = new MeshStandardMaterial({
      map: decalTex,
      transparent: true,
      polygonOffset: true,
      polygonOffsetFactor: -4,
    });

    const decalMesh = new Mesh(decalGeometry, decalMaterial);
    target.add(decalMesh);
  }

  loadTrain() {}

  moveTrain() {}

  createTracks() {
    // one track is 6.5 units
    // engine is ~ 6.5 units
    // boggie is 6 units
    // gap 0.5 units
    // so train = 6.5 + ( 0.5 + 6.0 ) * 3 = 26 units
    // initial track scale = 4
    // track length multiple of 6.5 * 4 = 26. lets start with 10

    this.resource.track.scene.traverse((c) => {
      if (c instanceof Mesh) {
        c.material = new MeshBasicMaterial({ color: "#2e2e2e" });
        c.position.set(0, 0, 0);
      }
    });

    const track1 = this.resource.track.scene.clone();
    const track2 = this.resource.track.scene.clone();
    const trackLengthScale = 10;

    track1.scale.z = trackLengthScale;
    track1.scale.x = 0.75;
    track2.scale.z = trackLengthScale;
    track2.scale.x = 0.75;

    track1.position.x = 0.4;
    track2.position.x = -0.4;

    const trackPair = new Group();

    trackPair.add(track1);
    trackPair.add(track2);

    trackPair.position.set(0, 0, (-trackLengthScale * 6.5) / 2 + 3.5);

    this.scene.add(trackPair);
  }

  createFreezingVapours() {
    // const posArray = new Float32Array(300);

    // for (let i = 0; i < 100; i++) {
    //   const i3 = i * 3;

    //   posArray[i3] = Math.random() - 0.5;
    //   posArray[i3 + 2] = Math.random() - 0.5;
    //   posArray[i3 + 1] = Math.random() - 0.5;
    // }

    // const geometry = new BufferGeometry()
    // geometry.setAttribute("position", new Float32BufferAttribute(posArray, 3))

    // const material = new PointsNodeMaterial()

    // this.particles = new Points(geometry, material);
    // this.scene.add(this.particles);
    this.imeshCount = 300;
    const geometry = new PlaneGeometry(0.6, 0.6);
    const material = new MeshBasicNodeMaterial({
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    });

    // material.positionNode = billboarding();

    material.fragmentNode = Fn(() => {
      const seed = float(instanceIndex).mul(17.31);
      const noiseP = vec3(
        uv().x.mul(2.5).add(seed),
        uv().y.mul(2.5).add(seed),
        this.elapsedTime.mul(0.4).add(seed)
      );
      const n = mx_noise_float(noiseP).mul(0.5).add(0.6);
      const radial = distance(uv(), vec2(0.5, 0.5)).mul(2).oneMinus().clamp();
      const alpha = radial.pow(1.4).mul(n).mul(0.22);
      If(alpha.lessThanEqual(0.02), () => {
        Discard();
      });
      return vec4(vec3(1), alpha);
      // return vec4(vec3(1), 0.1);
    })();

    this.freezingVapours = new InstancedMesh(
      geometry,
      material,
      this.imeshCount
    );

    //init
    const mat4 = new Matrix4();
    const position = new Vector3();
    const quaternion = new Quaternion();
    const scale = new Vector3();

    for (let i = 0; i < this.imeshCount; i++) {
      position.y = Math.random() * 0.5;
      position.z = -2.5 + Math.random() * 0.5;
      position.x = (i % 2 == 0 ? 1 : -1) * (0.4 + Math.random() * 0.5);

      quaternion.setFromEuler(
        new Euler(0,
          (i % 2 == 0 ? Math.PI / 3 : -Math.PI / 3) + (Math.random() - 0.5) * 0.3,
           0)
      );

      scale.x = scale.y = scale.z = Math.random() * 1;

      mat4.compose(position, quaternion, scale);

      this.freezingVapours.setMatrixAt(i, mat4);
      // this.freezingVapours.setColorAt(
      //   i,
      //   new Color().setHSL(
      //     1,
      //     0.5 + Math.random() * 0.5,
      //     0.5 + Math.random() * 0.5
      //   )
      // );
    }

    this.group.add(this.freezingVapours);
  }

  update() {
    this.elapsedTime.value = this.time.elapsed * 0.001;

    this._tickJump(Math.min(this.time.delta * 0.001, 0.05));

    if (this.freezingVapours) {
      // for (let i = 0; i < this.imeshCount; i++) {
      //   this.freezingVapours.get
      // }
      const v = new Vector3();
      const m = new Matrix4();
      const o = new Object3D();
      const q = new Quaternion();

      for (let i = 0; i < this.imeshCount; i++) {
        this.freezingVapours.getMatrixAt(i, m);
        m.decompose(o.position, q, v);
        o.quaternion.copy(q);
        o.position.y += 0.01 * Math.random();
        o.position.z -= 0.1 * Math.random();
        if (i % 2 == 0) {
          o.position.x += 0.01 * Math.random();
        } else {
          o.position.x -= 0.01 * Math.random();
        }

        let newScale = Math.max(0, v.x + 0.01);
        if (newScale >= 0.8) {
          newScale = Math.random();
          o.position.y = Math.random() * 0.5;
          o.position.z = -2.5 + Math.random() * 0.5;
          o.position.x = (i % 2 == 0 ? 1 : -1) * (0.4 + Math.random() * 0.5);
        }
        // if (newScale === 0) {
        //   console.log("cont", v.x)
        //   continue
        // }
        // if(v.x===1) console.log("Respawned", i)
        // if (v.x > 0 && newScale === 0) console.log(`particle ${i} just hit 0`)
        // if (v.x === 0 && newScale > 0) console.log(`particle ${i} RESPAWNED to ${newScale}`) // should never fire
        o.scale.setScalar(newScale);
        o.updateMatrix();
        this.freezingVapours.setMatrixAt(i, o.matrix);
      }
      this.freezingVapours.instanceMatrix.needsUpdate = true;
    }
  }
}
